import { useState, useEffect, useCallback } from "react";
import { Layers, Trash2, Download, ChevronDown, ChevronUp, Loader2, History, RotateCcw, Undo2, Eye, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

/**
 * Jun-2026 (User-Wunsch) — DATEV-Buchungsstapel.
 * Listet die gesammelten (KI/manuellen) Buchungssätze, erlaubt Löschen und den
 * DATEV-EXTF-Export PRO KONTENRAHMEN (ein Stapel = ein Kontenrahmen).
 * reloadSignal: Zähler — bei Änderung wird neu geladen (nach Hinzufügen).
 */
export default function BuchungsstapelPanel({ reloadSignal = 0 }) {
    const [open, setOpen] = useState(true);
    const [items, setItems] = useState([]);
    const [exports, setExports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewId, setPreviewId] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [editing, setEditing] = useState(null); // { id, beleg_nr, belegdatum }

    const toggleSel = (id) => setSelected((s) => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });

    const toggleGroup = (list) => setSelected((s) => {
        const n = new Set(s);
        const allSel = list.every((it) => n.has(it.id));
        list.forEach((it) => (allSel ? n.delete(it.id) : n.add(it.id)));
        return n;
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [open, hist] = await Promise.all([
                api.get("/buchhaltung/buchungsstapel", { params: { only_open: true } }),
                api.get("/buchhaltung/buchungsstapel/exports", { params: { limit: 20 } }),
            ]);
            setItems(open.data?.items || []);
            setExports(hist.data?.items || []);
        } catch { setItems([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load, reloadSignal]);

    const remove = async (id) => {
        try {
            await api.delete(`/buchhaltung/buchungsstapel/${id}`);
            setItems((xs) => xs.filter((x) => x.id !== id));
            toast.success("Aus Stapel entfernt");
        } catch { toast.error("Löschen fehlgeschlagen"); }
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            const { data } = await api.patch(`/buchhaltung/buchungsstapel/${editing.id}`, {
                beleg_nr: editing.beleg_nr || "", belegdatum: editing.belegdatum || "",
            });
            setItems((xs) => xs.map((x) => (x.id === editing.id
                ? { ...x, beleg_nr: data.beleg_nr, belegdatum: data.belegdatum } : x)));
            setEditing(null);
            toast.success("Beleg aktualisiert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    const exportKr = async (kr) => {
        const list = items.filter((x) => x.kontenrahmen === kr && !x.exported);
        const cnt = list.length;
        const sum = list.reduce((a, it) => a + (Number(it.betrag_brutto) || 0), 0);
        try {
            const r = await api.get("/buchhaltung/buchungsstapel/export", {
                params: { kontenrahmen: kr, mark: true }, responseType: "blob",
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = `DATEV_Buchungsstapel_${kr}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${cnt} ${cnt === 1 ? "Satz" : "Sätze"} über ${eur(sum)} exportiert`);
            load();
        } catch (e) {
            toast.error(e.response?.status === 400 ? `Keine offenen Sätze für ${kr}` : "Export fehlgeschlagen");
        }
    };

    const redownload = async (exp) => {
        try {
            const r = await api.get(`/buchhaltung/buchungsstapel/exports/${exp.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = exp.filename || `DATEV_${exp.kontenrahmen}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Erneut heruntergeladen");
        } catch { toast.error("Download fehlgeschlagen"); }
    };

    const reopen = async (exp) => {
        try {
            const { data } = await api.post(`/buchhaltung/buchungsstapel/exports/${exp.id}/reopen`);
            toast.success(`${data.reopened} Buchungssätze wieder geöffnet`);
            load();
        } catch { toast.error("Wieder-Öffnen fehlgeschlagen"); }
    };

    const exportSelected = async () => {
        const ids = [...selected];
        if (ids.length === 0) return;
        const rows = items.filter((x) => selected.has(x.id));
        const sum = rows.reduce((a, it) => a + (Number(it.betrag_brutto) || 0), 0);
        try {
            const r = await api.post("/buchhaltung/buchungsstapel/export-selected",
                { ids, mark: true }, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "DATEV_Buchungsstapel_Auswahl.csv";
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`${ids.length} ${ids.length === 1 ? "Satz" : "Sätze"} über ${eur(sum)} exportiert`);
            setSelected(new Set());
            load();
        } catch (e) {
            // Fehlermeldung (z. B. gemischte Kontenrahmen) auslesen — Blob → Text
            let msg = "Export fehlgeschlagen";
            try { msg = JSON.parse(await e.response?.data?.text())?.detail || msg; } catch { /* noop */ }
            toast.error(msg);
        }
    };

    // Gruppierung je Kontenrahmen für Export-Buttons
    const byKr = items.reduce((acc, it) => {
        (acc[it.kontenrahmen] = acc[it.kontenrahmen] || []).push(it);
        return acc;
    }, {});

    return (
        <div className="bg-white border border-gray-200 rounded-sm" data-testid="buchungsstapel-panel">
            <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3" data-testid="buchungsstapel-toggle">
                <span className="flex items-center gap-2 font-bold text-sm">
                    <Layers className="w-4 h-4 text-[#111827]" /> DATEV-Buchungsstapel
                    {items.length > 0 && <span className="text-[10px] font-bold bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm" data-testid="buchungsstapel-count">{items.length}</span>}
                </span>
                {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {open && (
                <div className="px-4 pb-4 border-t border-gray-100">
                    {/* Export-Buttons je Kontenrahmen + Auswahl-Export */}
                    {Object.keys(byKr).length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 py-3" data-testid="buchungsstapel-exports">
                            {Object.entries(byKr).map(([kr, list]) => {
                                const allSel = list.length > 0 && list.every((it) => selected.has(it.id));
                                return (
                                    <div key={kr} className="flex items-center gap-1.5 border border-gray-200 rounded-sm pl-2" data-testid={`buchungsstapel-group-${kr}`}>
                                        <label className="flex items-center gap-1 text-[11px] font-bold text-gray-600 cursor-pointer" title={`Alle ${kr}-Sätze auswählen`}>
                                            <input type="checkbox" checked={allSel} onChange={() => toggleGroup(list)}
                                                className="w-3.5 h-3.5 accent-[#FFCC00] cursor-pointer" data-testid={`buchungsstapel-selectall-${kr}`} />
                                            Alle
                                        </label>
                                        <Button onClick={() => exportKr(kr)} variant="ghost" size="sm"
                                            className="rounded-sm gap-1.5 font-bold text-xs h-8" data-testid={`buchungsstapel-export-${kr}`}>
                                            <Download className="w-3.5 h-3.5" /> DATEV-Export {kr} ({list.length})
                                        </Button>
                                    </div>
                                );
                            })}
                            {selected.size > 0 && (
                                <Button onClick={exportSelected} size="sm"
                                    className="rounded-sm gap-1.5 font-bold text-xs bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] ml-auto"
                                    data-testid="buchungsstapel-export-selected">
                                    <Download className="w-3.5 h-3.5" /> Ausgewählte exportieren ({selected.size})
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Jun-2026 (User-Wunsch): Summenzeile je Kontenrahmen */}
                    {Object.keys(byKr).length > 0 && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 pb-3 border-b border-gray-100 mb-1" data-testid="buchungsstapel-summary">
                            {Object.entries(byKr).map(([kr, list]) => {
                                const sum = list.reduce((a, it) => a + (Number(it.betrag_brutto) || 0), 0);
                                return (
                                    <span key={kr} data-testid={`buchungsstapel-sum-${kr}`}>
                                        <b className="text-gray-700">{kr}</b>: {list.length} {list.length === 1 ? "Satz" : "Sätze"} · brutto {eur(sum)}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {loading ? (
                        <div className="py-6 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-6 text-center text-gray-400 text-sm" data-testid="buchungsstapel-empty">
                            Noch keine Buchungssätze im Stapel. Lege Vorschläge über „Zum Buchungsstapel" ab.
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100" data-testid="buchungsstapel-list">
                            {items.map((it) => (
                                <li key={it.id} data-testid={`buchungsstapel-row-${it.id}`}>
                                    <div className="py-2 flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)}
                                            className="w-4 h-4 accent-[#FFCC00] cursor-pointer shrink-0" data-testid={`buchungsstapel-select-${it.id}`} />
                                        <span className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-sm">{it.kontenrahmen}</span>
                                        <span className="flex-1 min-w-0 truncate text-gray-700">{it.buchungstext || "—"}</span>
                                        <span className="hidden md:inline text-[10px] text-gray-400 font-mono-num" data-testid={`buchungsstapel-beleg-${it.id}`}>
                                            {it.beleg_nr ? `Beleg ${it.beleg_nr}` : "ohne Beleg-Nr."}{it.belegdatum ? ` · ${it.belegdatum}` : ""}
                                        </span>
                                        <span className="font-mono-num text-xs text-[#111827]">{it.soll_konto}→{it.haben_konto}</span>
                                        {it.steuerschluessel && <span className="text-[10px] text-gray-400">BU {it.steuerschluessel}</span>}
                                        <span className="font-mono-num text-xs text-gray-500 w-20 text-right">{eur(it.betrag_brutto)}</span>
                                        <button onClick={() => setEditing(editing?.id === it.id ? null : { id: it.id, beleg_nr: it.beleg_nr || "", belegdatum: it.belegdatum || "" })}
                                            className="text-gray-300 hover:text-[#111827]" title="Belegnummer/-datum bearbeiten" data-testid={`buchungsstapel-edit-${it.id}`}>
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => remove(it.id)} className="text-gray-300 hover:text-red-600" title="Entfernen" data-testid={`buchungsstapel-del-${it.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {editing?.id === it.id && (
                                        <div className="pb-3 flex flex-wrap items-end gap-2 bg-gray-50 border border-gray-100 rounded-sm p-2 mb-1" data-testid={`buchungsstapel-edit-panel-${it.id}`}>
                                            <label className="flex flex-col gap-0.5 text-[10px] font-bold uppercase text-gray-500">
                                                Belegnummer
                                                <input type="text" value={editing.beleg_nr} maxLength={40}
                                                    onChange={(e) => setEditing((s) => ({ ...s, beleg_nr: e.target.value }))}
                                                    className="border border-gray-300 rounded-sm px-2 py-1 text-sm font-normal normal-case w-44"
                                                    placeholder="z. B. RE-2026-0001" data-testid={`buchungsstapel-edit-belegnr-${it.id}`} />
                                            </label>
                                            <label className="flex flex-col gap-0.5 text-[10px] font-bold uppercase text-gray-500">
                                                Belegdatum
                                                <input type="date" value={editing.belegdatum}
                                                    onChange={(e) => setEditing((s) => ({ ...s, belegdatum: e.target.value }))}
                                                    className="border border-gray-300 rounded-sm px-2 py-1 text-sm font-normal" data-testid={`buchungsstapel-edit-belegdatum-${it.id}`} />
                                            </label>
                                            <Button onClick={saveEdit} size="sm" className="rounded-sm gap-1 font-bold text-xs bg-[#111827] hover:bg-black text-white h-8" data-testid={`buchungsstapel-edit-save-${it.id}`}>
                                                <Check className="w-3.5 h-3.5" /> Speichern
                                            </Button>
                                            <Button onClick={() => setEditing(null)} variant="ghost" size="sm" className="rounded-sm gap-1 font-bold text-xs h-8" data-testid={`buchungsstapel-edit-cancel-${it.id}`}>
                                                <X className="w-3.5 h-3.5" /> Abbrechen
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Jun-2026 (User-Wunsch): Exportverlauf — frühere DATEV-Stapel erneut laden */}
                    {exports.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="buchungsstapel-export-history">
                            <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                <History className="w-3.5 h-3.5" /> Exportverlauf
                            </div>
                            <ul className="divide-y divide-gray-100" data-testid="export-history-list">
                                {exports.map((e) => (
                                    <li key={e.id} className="py-2" data-testid={`export-history-row-${e.id}`}>
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-sm">{e.kontenrahmen}</span>
                                            <span className="flex-1 truncate text-gray-700">{e.count} {e.count === 1 ? "Satz" : "Sätze"} · {e.date_from}–{e.date_to}</span>
                                            {e.reopened && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm">wieder geöffnet</span>}
                                            <span className="text-gray-400 hidden lg:inline">{fmtDate(e.exported_at)}</span>
                                            <button onClick={() => setPreviewId(previewId === e.id ? null : e.id)} className="inline-flex items-center gap-1 text-gray-500 hover:text-[#111827]" title="Vorschau" data-testid={`export-history-preview-${e.id}`}>
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => reopen(e)} className="inline-flex items-center gap-1 text-amber-700 hover:underline font-bold" title="Stapel wieder öffnen" data-testid={`export-history-reopen-${e.id}`}>
                                                <Undo2 className="w-3.5 h-3.5" /> Öffnen
                                            </button>
                                            <button onClick={() => redownload(e)} className="inline-flex items-center gap-1 text-[#111827] hover:underline font-bold" title="Erneut herunterladen" data-testid={`export-history-download-${e.id}`}>
                                                <RotateCcw className="w-3.5 h-3.5" /> Laden
                                            </button>
                                        </div>
                                        {previewId === e.id && (
                                            <div className="mt-2 bg-gray-50 border border-gray-100 rounded-sm p-2 space-y-1" data-testid={`export-history-preview-body-${e.id}`}>
                                                {(e.entries_preview || []).length === 0 ? (
                                                    <div className="text-[11px] text-gray-400">Keine Vorschau verfügbar.</div>
                                                ) : (e.entries_preview).map((p, i) => (
                                                    <div key={i} className="flex items-center gap-2 text-[11px] text-gray-600">
                                                        <span className="font-mono-num text-[#111827]">{p.soll_konto}→{p.haben_konto}</span>
                                                        {p.steuerschluessel && <span className="text-gray-400">BU {p.steuerschluessel}</span>}
                                                        <span className="flex-1 truncate">{p.buchungstext || "—"}</span>
                                                        <span className="font-mono-num">{eur(p.betrag_brutto)}</span>
                                                    </div>
                                                ))}
                                                {e.count > (e.entries_preview || []).length && (
                                                    <div className="text-[10px] text-gray-400">… und {e.count - (e.entries_preview || []).length} weitere</div>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
